import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';

const baseLinkId = 'investments-faq-link';

// FAQ content
const ITEMS = [
  {
    id: 'investments-how-to-start-investing-faq',
    title: <>How can I get <WidowBlock>started investing&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Investing is a great first step toward building wealth for yourself and your family that can be used to reach your short&#8212;and long&#45;term your goals. And there are a lot of options to choose from like{' '}
        <Link
          id={`${baseLinkId}-mutual-funds`}
          linkUrl='/mutual-funds/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about low cost mutual funds'>
          mutual funds
        </Link>
        ,{' '}
        <Link
          id={`${baseLinkId}-etfs`}
          linkUrl='/etfs/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about ETFs to help your investments'>
          ETFs
        </Link>
        ,{' '}
        <Link
          id={`${baseLinkId}-equities-investments`}
          linkUrl='/equities-investments/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about equities investments'>
          individual stocks
        </Link>
        ,{' '}
        <Link
          id={`${baseLinkId}-bonds`}
          linkUrl='/bonds/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about bonds to help your investments'>
          bonds
        </Link>
        , and{' '}
        <Link
          id={`${baseLinkId}-cds`}
          linkUrl='/cds/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about CDs to help your investments'>
          CDs
        </Link>
        . However, successful investing should start with your investing goals, not what you&#39;re investing in. Our{' '}
        <Link
          id={`${baseLinkId}-advisors-asking-questions`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          financial advisors can help
        </Link>
        . They&#39;ll ask deeper questions, and listen closely, to better understand your goals and priorities, and how you think about money. Their expertise will help uncover your blind spots and opportunities that might otherwise get overlooked. Want to get a better handle on the basics first&#63;{' '}
        <Link
          id={`${baseLinkId}-beginners-guide`}
          linkUrl='/life-and-money/how-to-invest-a-beginners-guide/'
          variant='primaryDeemphasize'>
          Check out our investing guide for beginners
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-open-an-investment-account-faq',
    title: <>How do I open an <WidowBlock>investment account&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        All it takes is a conversation with one of our financial advisors. We understand that talking finances might not come naturally for you, so you&#39;ll be happy to know that our advisors are expert listeners who keep the conversation free of any jargon or judgments. We&#39;ll look at all aspects of your financial life, then create tailored investment strategies within your financial plan designed to help you reach your goals now, and down the road. Sound good&#63;{' '}
        <Link
          id={`${baseLinkId}-start-investing`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          Start investing with us by getting matched with an advisor
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-how-much-money-faq',
    title: <>How much money do I need to <WidowBlock>start investing&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Probably not as much as you think. Whether you&#39;re a beginner or a bit more experienced,{' '}
        <Link
          id={`${baseLinkId}-get-matched`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          talking to a financial advisor
        </Link>
        {' '}can help. They&#39;ll be able to recommend the right options based on the amount of money you&#39;re looking to invest, risk tolerance, time horizon, and goals.{' '}
        <Link
          id={`${baseLinkId}-jump-into-investing`}
          linkUrl='/life-and-money/how-to-start-investing/'
          variant='primaryDeemphasize'>
          See the simple ways you can jump into investing
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-401k-at-work-faq',
    title: <>Do I need a separate investment account if I have a 401(k) <WidowBlock>at work&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Contributing to a{' '}
        <Link
          id={`${baseLinkId}-401k-at-work`}
          linkUrl='/401k-plans/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about how a 401(k) works'>
          401&#40;k&#41;
        </Link>
        {' '}is a smart way to save for retirement. But there are other investment accounts that can help add to your retirement savings and your other goals, too. For example, an{' '}
        <Link
          id={`${baseLinkId}-iras-and-retirement-plans-at-work`}
          linkUrl='/iras-and-retirement-plans/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about IRA and retirement plans'>
          IRA
        </Link>
        {' '}in addition to your 401&#40;k&#41; can help maximize opportunities to save for retirement and give you more flexibility along the way. A{' '}
        <Link
          id={`${baseLinkId}-529-plan-at-work`}
          linkUrl='/529-college-savings-plans/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about 529 college savings plans'>
          529 plan
        </Link>
        {' '}can help you save &#40;in a tax&#45;advantaged way&#41; for a child&#39;s education expenses. And a{' '}
        <Link
          id={`${baseLinkId}-brokerage-accounts-services-at-work`}
          linkUrl='/investments/brokerage-accounts-services/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about brokerage account services'>
          brokerage account
        </Link>
        {' '}can help you grow your money for a major purchase like a new home, boat, or once&#45;in&#45;a&#45;lifetime trip. Ready to start investing&#63;{' '}
        <Link
          id={`${baseLinkId}-lets-talk`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          Talk to one of our financial advisors
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-best-investment-strategies-faq',
    title: <>What are the best <WidowBlock>investment strategies&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <>
        <Typography nextElement='typography'>
          The best investment strategies for you will depend on your goals, risk tolerance, and time horizon. However, there are some universally acclaimed approaches like{' '}
          <Link
            id={`${baseLinkId}-diversification`}
            linkUrl='/life-and-money/the-best-way-to-invest-money/'
            variant='primaryDeemphasize'
            ariaLabel='Learn more about the diversification of your investments'>
            diversification
          </Link>
          , which spreads investments across various asset classes to mitigate risk.{' '}
          <Link
            id={`${baseLinkId}-dollar-cost-averaging`}
            linkUrl='/life-and-money/is-dollar-cost-averaging-better-than-lump-sum-investing/'
            variant='primaryDeemphasize'
            ariaLabel='Learn more about the differences of dollar cost averaging compared to other kinds of investing'>
            Dollar&#45;cost averaging
          </Link>
          {' '}is another option where regular, smaller investments are made regardless of market conditions to reduce the impact of volatility. Then there&#39;s long&#45;term investing, which capitalizes on the growth potential of assets over time. Additionally, a focus on low&#45;cost index{' '}
          <Link
            id={`${baseLinkId}-low-cost-mutual`}
            linkUrl='/mutual-funds/'
            variant='primaryDeemphasize'
            ariaLabel='Learn more about low cost mutual funds'>
            mutual funds
          </Link>
          {' '}and{' '}
          <Link
            id={`${baseLinkId}-more-etfs`}
            linkUrl='/etfs/'
            variant='primaryDeemphasize'
            ariaLabel='Learn more about ETFs to help your investments'>
            ETFs
          </Link>
          {' '}can give you broad market exposure while helping to minimize fees.
        </Typography>
        <Typography disableBottomPadding>
          Looking for more guidance&#63; Talk to one of our{' '}
          <Link
            id={`${baseLinkId}-find-guidance-with-advisor`}
            linkUrl='/find-a-financial-advisor/'
            variant='primaryDeemphasize'>
            financial advisors
          </Link>
          . They&#39;ll ask deeper questions to better understand your goals and priorities, and how you think about money. Their expertise will help uncover your blind spots and opportunities that might otherwise get overlooked.
        </Typography>
      </>
    ),
  },
  {
    id: 'investments-best-investment-products-faq',
    title: <>What are the best types of <WidowBlock>investment products&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        The best options for you will depend on your financial goals, risk tolerance, tax status, and amount of money you&#39;re looking to invest. From{' '}
        <Link
          id={`${baseLinkId}-advisory`}
          linkUrl='/investments/investment-advisory-services/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about investment advisory services'>
          advisory
        </Link>
        {' '}and{' '}
        <Link
          id={`${baseLinkId}-brokerage`}
          linkUrl='/investments/brokerage-accounts-services/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about brokerage accounts services'>
          brokerage
        </Link>
        {' '}services to{' '}
        <Link
          id={`${baseLinkId}-private-wealth-management`}
          linkUrl='/private-wealth-management/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about private wealth management services'>
          private wealth management
        </Link>
        , our financial advisors can help you put together a financial plan that incorporates the right investment strategies and options to help you reach your goals now, and down the road.
      </Typography>
    ),
  },
  {
    id: 'investments-tax-advantaged-investment-options-faq',
    title: <>What tax&#45;advantaged investment options <WidowBlock>are there&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        There are several tax&#45;free and tax&#45;advantaged accounts like an IRA (tax&#45;advantaged), 529 education savings plan (tax free), and tax&#45;free&#47;tax&#45;advantaged investment products, such as municipal bonds. Tax&#45;free investment products like municipal bonds can be held in both taxable and tax&#45;free investment accounts.{' '}
        <Link
          id={`${baseLinkId}-talk-to-an-advisor`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          Talk to an advisor about which types of investments are right for your goals
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-least-risky-investments-faq',
    title: <>What are the least <WidowBlock>risky investments&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Investments such as CDs and money&#45;market funds<sup>1</sup> are designed to not lose value, but you risk losing the potential gains of growth investments like stocks<sup>2</sup>, mutual funds, or ETFs. A financial advisor can help you create a diversified portfolio that minimizes your overall risk while allowing for potential gains.{' '}
        <Link
          id={`${baseLinkId}-risk-investments`}
          linkUrl='/life-and-money/common-investing-risks-and-how-to-manage-them/'
          variant='primaryDeemphasize'>
          Learn more about common investment risks and how you can manage them
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-least-risky-investments-faq',
    title: <>Is it better to invest with an advisor or on <WidowBlock>my own&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Choosing between investing with an advisor or on your own depends on your financial knowledge, time commitment, and confidence. An advisor can give you personalized advice, help diversify your portfolio, and assist with complex strategies, making them valuable for those unfamiliar with investing or with substantial assets. A{' '}
        <Link
          id={`${baseLinkId}-advisor-goes-further`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          Northwestern Mutual financial advisor
        </Link>
        {' '}goes even further. They ask deeper questions to better understand your goals and priorities, and how you think about money. And their expertise will help uncover your blind spots and opportunities that others often overlook. Want to know more about the advantages of working with an advisor&#63;{' '}
        <Link
          id={`${baseLinkId}-know-the-advantages`}
          linkUrl='/life-and-money/investment-advisor/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about advantages of an investment advisor'>
          Find out here
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-personalized-investing-advice-faq',
    title: <>Can I get personalized investing advice if I only have a small amount <WidowBlock>to invest&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        Yes. No matter how much (or little) you&#39;re looking to invest, our financial advisors will help you navigate your choices and recommend the right investments for your goals. For those who have more significant amounts to invest and are looking for hands&#45;on investment advice and ongoing oversight, our{' '}
        <Link
          id={`${baseLinkId}-advisory-services-smart`}
          linkUrl='/investments/investment-advisory-services/'
          variant='primaryDeemphasize'
          ariaLabel='Learn more about smart investment advisory services'>
          Advisory Services
        </Link>
        {' '}is a smart way to go. Ready to start investing&#63;{' '}
        <Link
          id={`${baseLinkId}-personalized-investing-advice-fafa-link`}
          linkUrl='/find-a-financial-advisor/'
          variant='primaryDeemphasize'>
          Talk to one of our financial advisors
        </Link>.
      </Typography>
    ),
  },
  {
    id: 'investments-accounts-protected-faq',
    title: <>Are investment accounts protected in the event that a <WidowBlock>firm fails&#63;</WidowBlock></>,
    contentAs: 'div',
    content: (
      <Typography disableBottomPadding>
        While investments can lose value due to investment risk, the Securities Investor Protection Corporation &#40;SIPC&#41; protects accounts at member firms &#40;like Northwestern Mutual Investment Services, LLC&#41;, up to &#36;500,000 &#40;including &#36;250,000 for claims for cash&#41;, in the event that a member firm fails.{' '}
        <Link
          id={`${baseLinkId}-find-out-more`}
          linkUrl='/investments/protection/'
          variant='primaryDeemphasize'
          ariaLabel='Find out more about protecting your investments'>
          Find out more
        </Link>.
      </Typography>
    ),
  },
];

export default () => (
  <SectionContainer
    id='investments-faq' // '-section-container' will be appended dynamically
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={5}>
        <Typography
          component='h2'
          nextElement='typography'>
          <Typography
            component='span'
            disableBottomPadding
            block>
            You&#39;ve got <WidowBlock>investing questions,</WidowBlock>
          </Typography>
          <Typography
            component='span'
            disableBottomPadding
            block>
            we&#39;ve got answers.
          </Typography>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-investments'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={ITEMS}
        />
      </Col>
    </Row>
  </SectionContainer>
);
